import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import type { MetaStateInReducer } from './slice'
import {
  getAddressFormatting,
  getCTAConfig,
  getDetailMenuSite,
  getListOfMenuSite,
  getMenuFeaturesAction,
  getScriptsApi,
  getStripeTokenApi,
  getTrackerAPi,
  RequestType,
  ResponseType,
  getMaxDaysPickerConfig,
  getAnnouncementBarConfig
} from 'src/services/oms'
import { toJSON } from 'src/utils/cookie'
import { extract } from 'src/utils/object'
import { clearCacheMenu } from 'src/services/cache'

export const fetchListOfMenuSite = createAsyncThunk(
  '@meta/menus',
  async (
    {
      slug,
      req,
      res,
      query
    }: {
      slug: string
      req: RequestType
      res?: ResponseType
      query?: Record<string, string | string[]>
    },
    { rejectWithValue }
  ) => {
    try {
      const result = await getListOfMenuSite({ slug, req, query })
      if (res && !res.headersSent) {
        const cookieFields = ['s_g_m_i', 's_g_rwg', 'g_m_i', 'g_rwg']
        ;(result?.headers?.['set-cookie'] || []).map((cookie) => {
          const parsed = toJSON(cookie)
          const extractData = extract(parsed, cookieFields)
          if (Object.keys(extractData).length > 0) {
            const cookieName = Object.keys(extractData)[0]
            res.cookie(cookieName, parsed[cookieName], {
              sameSite: 'none',
              secure: true,
              ...(parsed.Expires
                ? {
                    expires: new Date(parsed.Expires)
                  }
                : {}),
              ...(parsed.Domain
                ? {
                    domain: parsed.Domain
                  }
                : {})
            })
          }
        })
      }
      if (result?.data) return result.data
      throw result
    } catch (err) {
      // Use `err.response.data` as `action.payload` for a `rejected` action,
      // by explicitly returning it using the `rejectWithValue()` utility
      return rejectWithValue(err.response.data)
    }
  }
)

export const fetchDetailMenuSite = createAsyncThunk(
  '@meta/menus/detail',
  async (
    {
      slug,
      menu,
      req,
      res,
      mode
    }: {
      slug: string
      menu: Meta.MenuSite
      req: RequestType
      res: ResponseType
      mode: string
    },
    { rejectWithValue }
  ) => {
    try {
      const result = await getDetailMenuSite({
        slug,
        menuId: menu.id,
        req,
        mode
      })

      const resData = {}

      const cookieFields = [
        'storeOrderId',
        'menuId',
        'hasPickup',
        'hasDelivery',
        'lang',
        'userInfo',
        '_o',
        'customerSessionId',
        'enableOddlePass',
        'SESSION'
      ]

      if (!res?.headersSent) {
        ;(result?.headers?.['set-cookie'] || []).map((cookie) => {
          const parsed = toJSON(cookie)
          const extractData = extract(parsed, cookieFields)
          if (Object.keys(extractData).length > 0) {
            const cookieName = Object.keys(extractData)[0]
            resData[cookieName] = parsed[cookieName]
            res.cookie(cookieName, parsed[cookieName], {
              sameSite: 'none',
              secure: true,
              ...(parsed.Expires
                ? {
                    expires: new Date(parsed.Expires)
                  }
                : {})
            })
          }
        })
      }

      return {
        meta: result.data.meta,
        data: {
          ...result.data.data,
          ...resData
        },
        errors: result.data.errors,
        mode
      }
    } catch (err) {
      // Use `err.response.data` as `action.payload` for a `rejected` action,
      // by explicitly returning it using the `rejectWithValue()` utility
      return rejectWithValue({ ...err.response?.data, mode } || err)
    }
  }
)

export const setBotMetaAction = ({
  isBot
}: {
  isBot: boolean
}): { type: string; payload: { isBot: boolean } } => {
  return {
    type: '@meta/set-bot-meta',
    payload: { isBot: isBot }
  }
}

export const fetchMenuFeatures = createAsyncThunk(
  '@meta/menus/features',
  async (
    {
      isServer,
      features,
      req
    }: {
      isServer: boolean
      features: string[]
      req: RequestType
    },
    thunkApi
  ) => {
    try {
      const state = thunkApi.getState() as MetaStateInReducer
      const menuId = state['@meta'].detailMenuSite.data.menu.id

      const result = await getMenuFeaturesAction({
        menuId,
        features,
        isServer,
        req
      })
      return result.data
    } catch (err) {
      console.log(err)
      // Use `err.response.data` as `action.payload` for a `rejected` action,
      // by explicitly returning it using the `rejectWithValue()` utility
      return thunkApi.rejectWithValue(err.response?.data || err)
    }
  }
)

export const fetchMetaAddressFormat = createAsyncThunk(
  '@meta/menus/address-format',
  async (
    {
      isServer,
      req
    }: {
      isServer: boolean
      req: RequestType
    },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const menuId = state['@meta'].detailMenuSite.data.menu.id

    const result = await getAddressFormatting({ menuId, isServer, req })
    return result.data
  }
)

export const fetchTrackerApi = createAsyncThunk(
  '@meta/menus/tracker-api',
  async (
    {
      isServer,
      req
    }: {
      isServer: boolean
      req: RequestType
    },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const menuId = state['@meta'].detailMenuSite.data.menu.id

    const result = await getTrackerAPi({ menuId, isServer, req })
    return result.data
  }
)

export const fetchScriptsApi = createAsyncThunk(
  '@meta/menus/scripts-api',
  async (
    {
      isServer,
      req
    }: {
      isServer: boolean
      req: RequestType
      pageName?: string
    },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const menuId = state['@meta'].detailMenuSite.data.menu.id

    const result = await getScriptsApi({ menuId, isServer, req })
    return result.data
  }
)

export const fetchStripeTokenApi = createAsyncThunk(
  '@meta/menus/stripe-token',
  async (params, thunkApi) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const menuId = state['@meta'].detailMenuSite.data.menu.id
    const testAccount = state['@user'].user.data.testAccount
    const result = await getStripeTokenApi({ menuId, testAccount })

    return result.data
  }
)

export const initializedOPWAction = createAction('@meta/initializedOPWAction')

export const setMetaAddressFormatCache = createAction(
  '@meta/setMetaAddressFormatCache',
  (params) => {
    return {
      payload: params
    }
  }
)

export const setForStripeTokenCache = createAction(
  '@meta/setForStripeTokenCache',
  (params) => {
    return {
      payload: params
    }
  }
)

export const setCacheForTrackerCache = createAction(
  '@meta/setCacheForTrackerCache',
  (params) => {
    return {
      payload: params
    }
  }
)

export const setScriptsApiCache = createAction(
  '@meta/setScriptsApiCache',
  (params) => {
    return {
      payload: params
    }
  }
)

export const fetchCTAConfig = createAsyncThunk(
  '@meta/menus/cta-config',
  async () => {
    const result = await getCTAConfig()

    return result.data
  }
)

export const clearCacheMenuAction = createAsyncThunk(
  '@meta/menus/clearCacheMenuAction',
  async (params, thunkApi) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const menuName = state['@meta'].cartName

    const result = await clearCacheMenu({ menuName })

    return result.data
  }
)

export const fetchMaxDaysPickerConfig = createAsyncThunk(
  '@meta/menus/max-days-picker-config',
  async ({ menuName }: { menuName: string }) => {
    const result = await getMaxDaysPickerConfig()
    return typeof result?.data?.[menuName] === 'number'
      ? result?.data?.[menuName]
      : null
  }
)

export const fetchAnnouncementBarConfig = createAsyncThunk(
  '@meta/menus/announcement-bar-config',
  async ({ menuCountry, isBeta }: { menuCountry: string; isBeta: boolean }) => {
    const result = await getAnnouncementBarConfig(isBeta)
    return result?.data?.[menuCountry] || null
  }
)
